import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Grid, ListItemIcon, Typography, useMediaQuery, useTheme, MenuItem } from "@mui/material";
import Card from "../../common/Card";
import Table, {Row as TableRow} from "../../common/CustomTable";
import Header from "../../common/Header";
import Button from "../../common/Button";
import DifferenceIcon from '@mui/icons-material/Difference';
import {
  AddCircleOutlineOutlined, AppRegistrationOutlined, CloudDownload, DeleteOutlineOutlined, DownloadOutlined, ExitToApp, KeyboardArrowDown, ListAltOutlined, ModeOutlined,
  SearchOutlined,
  TrackChanges,
  Troubleshoot
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ProductInfo } from '../../../redux/slice/product/productListSlice';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { PracticeInfo } from '../../../redux/slice/practiceSlice';
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from '../../../redux/slice/authSlice';
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, IVR_STATUS, IVR_STATUS_LIST, MANUFACTURE_ACTIONS, ORDERTYPE, USER_ROLES } from '../../../constants/applicationConstants';
import { formatDate, fullName, hideElement, sortNames } from '../../../utils/Utility';
import { createListSort } from '../../../utils/listSort';
import {
  fetchManufacturerDataById,
  fetchManufacturerList, resetManufacturerList, SearchManufacturerRequest, useManufacturerListSlice
} from '../../../redux/slice/manufacturer/ManufacturerSlice';
// import IVRPracticeSelectionPopup from './IVRPracticeSelectionPopup';
import CustomIconButton from '../../common/CustomIconButton';
import { CodeTable, fetchAllCodeTablesValues, getCodeTableValuesByTableName, useCodetablesSlice } from '../../../redux/slice/codetableSlice';
import { fetchManufacturersList, setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo, updateElementInCommonSlice, useCommonReducer } from '../../../redux/slice/commonSlice';
import { RootState } from '../../../redux/store';
import { useNavigation } from '../../../hooks/useNavigation';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import CustomTextField from '../../common/CustomTextField';
import { useForm } from 'react-hook-form';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import CustomPatternTextField from '../../common/CustomPatternTextField';
import CustomDropdownField from '../../common/CustomDropdownFiled';
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage } from '../../../utils/StorageUtils';
import StyledMenu from '../../common/StyledMenu';
import ExportToExcel from '../../common/ExportToExcel';
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface Row {
  contactPhone: string;
  manufacturerName: string | JSX.Element;
  manufacturerNameText: string;
  // notificationEmail: string;
  inactiveDate: string;
  action: JSX.Element | null;
  manufacturerId: number;
  legalName: string;
}

const manuStatusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" }
];

const pharmacyFilterOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" }
];

const toPharmacyFlag = (value?: string | null): boolean | null =>
  value === "true" ? true : value === "false" ? false : null;

const toPharmacyFilterValue = (flag?: boolean | null): string =>
  flag === true ? "true" : flag === false ? "false" : "";

// Define columns and rows data
const columns: Column[] = [
  { id: "manufacturerName", label: "Manufacturer ", minWidth: 100 },
  { id: "contactPhone", label: "Contact Phone", minWidth: 100 },
  { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
  { id: "legalName", label: "Legal Name", minWidth: 100 },
  // { id: 'notificationEmail', label: 'Notification Email', minWidth: 100 },
  { id: "action", label: "Action", minWidth: 50, },
];

/**
 * CS-2075 — the pharmacy dropdown is tri-state ("" / "true" / "false"), so the form
 * carries a string where the request carries boolean | null.
 */
type ManufacturerFilterForm = Omit<SearchManufacturerRequest, "isPharmacy"> & {
  isPharmacy?: string;
};

const ITEMS_PER_PAGE = 20;

const manufacturerListSort = createListSort(
  ["manufacturerName", "inactiveDate", "legalName"] as const,
  {
    manufacturerName: "manufacturerName",
    inactiveDate: "inactiveDate",
    legalName: "legalName",
  }
);

const ManufacturerList: React.FC = () => {
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const canAuditManufacture = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.MANUFACTUREAUDIT)(state));
  const canCreateManufacture = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessManufactureAction", MANUFACTURE_ACTIONS.CREATE)(state));
  const canEditManufacture = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessManufactureAction", MANUFACTURE_ACTIONS.EDIT)(state));
  // CS-1685 - Start
  const canExportManufacture = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.MANUFACTURER)(state));
  const {userInfo} = useAuthSlice();
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  // CS-1685 - End

  const { manufacturerInfoList } = useCommonReducer();
  const { ManufacturerListInfo } = useManufacturerListSlice();
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const { control, handleSubmit, reset, setValue, getValues } = useForm<ManufacturerFilterForm>();
  const [searchCriteria, setSearchCriteria] = useState<SearchManufacturerRequest>({
    mfgIds: [],
    contactPhone: "",
    manufacturerStatus:[],
    isPharmacy: null,
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
  });
  const location = useLocation();
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null); // CS-1555

  // --- Design tokens ---------------------------
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg    = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg    = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 1, "& svg": { fontSize: "18px" } },
    "&:hover": { color: "#fff", borderColor: "#2f9b8f", backgroundColor: "#2f9b8f", boxShadow: "0 10px 24px rgba(20,184,166,0.28)" },
    "&.Mui-disabled": { color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)", borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)", backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)" },
  };

  const manufacturerTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };
  // -----------------------------------------------------------------------------

  // CS-1555 - Start
  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) setActiveButtonId(saved);
  }, []);

  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "manufacturerList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  };

  const handleRowNavigateToEdit = (row: TableRow) => {
    const manufacturerId = row.manufacturerId;
    if (!manufacturerId) return;
    handleActionButtonClick(`manufacturerListActionButton-${manufacturerId}`, () =>
      handleActionEdit(row)
    );
  };
  // CS-1555 - End

  const handleActionEdit = (row: any) => {
    const { notificationEmail, customConfig, contactPhone, manufacturerId } = row;
    // CS-2075 — the name cell can be JSX, so read the plain name kept alongside it.
    const manufacturerName = row.manufacturerNameText ?? row.manufacturerName;
    dispatch(fetchManufacturerDataById(parseInt(manufacturerId)));
    navigate("/manufacturer_create", {
      state: { mfrData: { manufacturerName, notificationEmail, customConfig, contactPhone, manufacturerId } },
    });
  }
  // CS-1685 - Start
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  const handleExport = (exportType: string) => { 
    if (searchCriteria) {
      let dataToSearch = {
        ...searchCriteria,
        currentPage: searchCriteria.currentPage,
        downloadState: true
      }
      ExportToExcel(
        dataToSearch,
        columns,
        dispatch,
        exportType === "details" ? EXPORTABLE_ITEMS.MANUFACTURER_DETAILS : EXPORTABLE_ITEMS.MANUFACTURER
      );
    }
    handleMenuClose();
  };
  // CS-1685 - End

  const actionComponent = (row: Row): JSX.Element | null => { // CS-1555

    if (!canEditManufacture && !canCreateManufacture) {
      return null;
    }

    return (
      <>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Edit"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canEditManufacture)}` }}
          // CS-1555 - Start
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(
              `manufacturerListActionButton-${row.manufacturerId}`,
              () => handleActionEdit(row)
            )
          }} // CS-1555 - End
        >
          <ModeOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Manufacturer Audit"
          color="warning"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canAuditManufacture)}` }}
          // CS-1555 - Start
          onClick={() =>
            handleActionButtonClick(
              `manufacturerListActionButton-${row.manufacturerId}`,
              () => handleClickManufactureAudit(row)
            )
          } // CS-1555 - End
          >
          <TrackChanges fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const formattedManufacturerInfo: Row[] = useMemo(() => {
    let manufacturers: Row[] = [];
    if (ManufacturerListInfo) {
      manufacturers = ManufacturerListInfo.content?.map((manufacturer: any) => ({
        manufacturerName: manufacturer.isPharmacy ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
            {manufacturer.manufacturerName}
            <Box
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                lineHeight: 1.6,
                px: 0.7,
                borderRadius: "6px",
                color: "#14B8A6",
                border: "1px solid rgba(20,184,166,0.55)",
                backgroundColor: "rgba(20,184,166,0.12)",
                whiteSpace: "nowrap",
              }}
            >
              Pharmacy
            </Box>
          </Box>
        ) : (
          manufacturer.manufacturerName
        ),
        manufacturerNameText: manufacturer.manufacturerName,
        inactiveDate: manufacturer.inactiveDate,
        contactPhone: manufacturer.contactPhone,
        manufacturerId: manufacturer.manufacturerId,
        legalName: manufacturer.legalName,
        // notificationEmail: manufacturer.notificationEmail != null ? JSON.stringify(manufacturer.notificationEmail).replaceAll(',', '\n') : '',
        action: actionComponent(manufacturer),
      }));
    };
    return manufacturers;
  }, [ManufacturerListInfo]);

  const clearReducers = () => {
    dispatch(updateElementInCommonSlice({
      elementName: "manufacturerInfoList",
      value: []
    }));
    dispatch(resetManufacturerList());
  }

  //Mount
  useEffect(() => {
    clearReducers();
    fetchInitialData();
    if(location?.state && location?.state?.manufactureSearchCriteria){
      setSearchCriteria(location?.state?.manufactureSearchCriteria)
      fetchInitialLists(location?.state?.manufactureSearchCriteria);
    } else {
      fetchInitialLists(searchCriteria);
    }
  }, []);

  const fetchInitialLists = async (data: SearchManufacturerRequest) => {
    const normalizedData = manufacturerListSort.normalize(data);
    if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
      dispatch(showHideLoadingDialog(true));
      await dispatch(fetchManufacturerList(normalizedData));
      dispatch(showHideLoadingDialog(false));
    }
  }

  const fetchInitialData = async () => {
    const searchFieldMfgResponse = await dispatch(fetchManufacturersList());
  }

  //Unmount
  useEffect(() => () => {
    clearReducers();
  }, []);

  const handleClickManufactureAudit = (data: any) => {
    // CS-2075 — the name cell can be JSX, so read the plain name kept alongside it.
    const manufacturerName = data?.manufacturerNameText ?? data?.manufacturerName;
    if (manufacturerName) {
      const MFRAuditData = {
        entityName: "MANUFACTURER",
        entityNumber: manufacturerName
      }
      const url = `/auditlog?entityName=${MFRAuditData.entityName}&entityNumber=${MFRAuditData.entityNumber}`;
      window.open(url, '_blank');
    }
  }

  const onSubmit = (data: ManufacturerFilterForm) => {
    const searchData: SearchManufacturerRequest = {
      mfgIds: data.mfgIds ? data.mfgIds : [],
      contactPhone: data.contactPhone ? data.contactPhone : "",
      manufacturerStatus: data.manufacturerStatus ? data.manufacturerStatus : [],
      // null when the filter is left blank, so the backend ignores it
      isPharmacy: toPharmacyFlag(data.isPharmacy),
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      ...manufacturerListSort.pick(searchCriteria),
    }
    setSearchCriteria(searchData);
    navigate("/manufacturer_list", { state: { manufactureSearchCriteria: searchData } });
  }

  useEffect(() => {
    if (location.state && location.state.manufactureSearchCriteria) {
      const searchData = location.state.manufactureSearchCriteria
      setValue("mfgIds", searchData.mfgIds)
      setValue("contactPhone", searchData.contactPhone)
      setValue("manufacturerStatus", searchData.manufacturerStatus)
      setValue("isPharmacy", toPharmacyFilterValue(searchData.isPharmacy))
      setValue("itemsPerPage", searchData.itemsPerPage)
      setValue("currentPage", searchData.currentPage)
      fetchInitialLists(searchData)
    }
  }, [location.state])

  const manufacturesList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    manufacturerInfoList?.length > 0 && manufacturerInfoList.map((item) => {
      options.push({
        label: item.manufacturerName,
        value: item.manufacturerId,
      });
    });

    if(manufacturerInfoList?.length === 1) {
      setValue("mfgIds", [manufacturerInfoList[0].manufacturerId]);
    };

    return options;
  }, [manufacturerInfoList]);

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1,
    };
    setSearchCriteria(data);
    navigate("/manufacturer_list", { state: { manufactureSearchCriteria: data } });
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = manufacturerListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/manufacturer_list", { state: { manufactureSearchCriteria: data } });
  };

  const totalRecords = ManufacturerListInfo?.totalElements ?? 0;

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* -- Filter panel -- */}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item md={3} xs={12}>
                    <CustomMultipleDropDown
                      control={control}
                      name="mfgIds"
                      label="Manufacturer"
                      placeholderText="Select manufacturer"
                      options={manufacturesList}
                      disabled={manufacturerInfoList?.length === 1}
                      size="small"
                    />
                  </Grid>
                  {/* CS-2075 — blank means "All", so no empty option is needed. */}
                  <Grid item md={12 / 5} xs={12}>
                    <CustomDropdownField
                      control={control}
                      name="isPharmacy"
                      label="Pharmacy"
                      placeholderText="Select"
                      options={pharmacyFilterOptions}
                      sortOptions={false}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={12 / 5} xs={12}>
                    <CustomPatternTextField
                      control={control}
                      name="contactPhone"
                      label="Contact Phone"
                    />
                  </Grid>
                  <Grid item md={11 / 5} xs={12}>
                    <CustomMultipleDropDown
                      control={control}
                      name="manufacturerStatus"
                      label="Status"
                      placeholderText="Select status"
                      options={manuStatusOptions}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* -- Main list card -- */}
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">Manufacturer List</Typography>
              </Grid>

              {/* Show / Hide filters */}
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={outlineBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>

              {/* Create Manufacturer */}
              {canCreateManufacture && (
                <Grid item xs={12} md="auto">
                  <Button
                    fullWidth
                    startIcon={<AddCircleOutlineOutlined />}
                    onClick={() => navigate("/manufacturer_create")}
                    sx={primaryActionBtnSx}
                    removeUpperCase
                  >
                    Create Manufacturer
                  </Button>
                </Grid>
              )}

              {/* CS-1685 - Export */}
              {canExportManufacture && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}

                  {isMobile && (
                    <Button
                      color="success"
                      variant="text"
                      onClick={(event) => handleMenuClick(event)}
                      startIcon={<DownloadOutlined fontSize="small" />}
                      sx={exportBtnSx}
                      removeUpperCase
                    >
                      Export
                    </Button>
                  )}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={() => handleExport("searchList")}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={() => handleExport("details")}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Details List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Grid>
              )}
              {/* CS-1685 - End */}
            </Grid>

            <Table
              columns={columns}
              rows={formattedManufacturerInfo}
              actionComponent={(row: Row) => actionComponent(row)} // CS-1555
              emptyText="No Manufacturer(s) Found"
              showMenu={true}
              totalElementText={
                "Records: " +
                (ManufacturerListInfo?.totalElements
                  ? ManufacturerListInfo?.totalElements
                  : "0")
              }
              pageNumber={handlePagination}
              sortableColumns={manufacturerListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={manufacturerListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              currentPageNumber={searchCriteria?.currentPage ? searchCriteria?.currentPage - 1 : 1}
              totalPages={ManufacturerListInfo?.totalPages}
              // CS-1555 - Start
              uniqueValue="manufacturerId"
              currentPageKey="manufacturerList"
              activeButtonId={activeButtonId}
              onRowClick={handleRowNavigateToEdit}
              clickableCards={true}
              // CS-1555 - End
              sx={manufacturerTableSx}
            />
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default ManufacturerList;